/**
 * SessionExpiredHandler
 *
 * Bridges the module-level `session-events` bus to the React tree:
 *  - Listens for "session expired" events emitted by the HTTP layer when a
 *    401 occurs AND the refresh-token attempt also fails.
 *  - Shows a session-expired toast (uses the existing `errors:token_expired`
 *    i18n key — same copy: "Tu sesión ha caducado. Por favor, inicia sesión
 *    de nuevo." / "Your session has expired. Please log in again.").
 *  - Clears AuthContext local state (storage is already wiped by ApiService).
 *  - Redirects the user to `/login`, but only if they are not already on a
 *    public/auth route — otherwise the navigation would interrupt the
 *    login form they're filling out.
 *
 * MUST be mounted inside `AppRouter` (for useNavigate/useLocation),
 * AuthProvider (for the logout dispatch), and NotificationProvider (for the
 * toast). This file is the ONLY consumer of the session-events bus inside
 * the React tree — all other code paths fire-and-forget via `emitSessionExpired`.
 */

import React, { useEffect, useRef } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { useLocation, useNavigate } from 'react-router-dom'

import { isAccountRefusalCode } from '@/platform/auth/services/account-refusal'
import {
  isSessionEndCode,
  SESSION_END_MESSAGE_KEYS,
} from '@/platform/auth/services/session-end-reason'
import { subscribeToSessionExpired } from '@/platform/auth/services/session-events'
import { useNotifications } from '@/shared/components/Notification'
import { useAuth } from '@/shared/contexts/AuthContext'

/**
 * Public/auth routes where a session-expired redirect would be disruptive
 * (e.g. the user is mid-flow on /login filling in credentials). On these
 * routes we still show the toast for visibility but skip the navigation.
 */
const PUBLIC_AUTH_ROUTE_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/magic-link',
  '/magic-login',
] as const

function isOnPublicAuthRoute(pathname: string): boolean {
  return PUBLIC_AUTH_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}

export const SessionExpiredHandler: React.FC = () => {
  const { t } = useTranslation('errors')
  const notifications = useNotifications()
  const { logout, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Latest-values ref so the (stable) subscription handler always reads the
  // current pathname / auth flag without having to resubscribe on every
  // navigation tick.
  const latestStateRef = useRef({
    pathname: location.pathname,
    isAuthenticated,
  })
  latestStateRef.current = {
    pathname: location.pathname,
    isAuthenticated,
  }

  useEffect(() => {
    const unsubscribe = subscribeToSessionExpired((reason) => {
      const { pathname, isAuthenticated: currentlyAuthenticated } = latestStateRef.current

      // Always show the toast — even on /login a stale write that 401s
      // deserves the explanation, so the user knows their session went away.
      // The namespace is already bound by `useTranslation('errors')` —
      // prefixing the key with `errors:` again routes i18next to look
      // under `errors.errors.token_expired`, which doesn't exist, so
      // it falls back to rendering the raw key (`token_expired`). Use
      // the bare key so the actual `errors.token_expired` string lands.
      // An ACCOUNT refusal is not an expiry: say which door closed (the login
      // page repeats it, since this toast is gone before it paints).
      notifications.error(
        isAccountRefusalCode(reason)
          ? t(`auth:codes.${reason}`)
          : isSessionEndCode(reason)
            ? t(SESSION_END_MESSAGE_KEYS[reason])
            : t('token_expired'),
      )

      // Tear down AuthContext state. ApiService.clearAuthLocally() already
      // wiped storage; this triggers the LOGOUT reducer + clears React Query
      // cache so stale data doesn't leak after re-login. Fire and forget —
      // the logout endpoint will 401 itself but that's expected.
      if (currentlyAuthenticated) {
        void logout()
      }

      // Don't yank the user off /login or another public route mid-flow.
      if (!isOnPublicAuthRoute(pathname)) {
        navigate('/login', { replace: true })
      }
    })
    return unsubscribe
  }, [logout, navigate, notifications, t])

  return null
}
